import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const QUEUE_KEY = ['sales', 'queue'];

// GET /sales/requests — "my queue". requestService.listMyQueue() returns
// requests already ordered oldest-first (idx_requests_queue / FIFO), so no
// client-side sort is needed here. Also returns `canAssign` (can_assign
// permission, utils/permissionCatalog.js) — lets pages/QueuePage.jsx decide
// whether to offer the reassign action per row, same "flag lets the client
// decide whether to show a whole action" shape as
// hooks/useApprovalDetail.js's sibling permission flags.
export function useSalesQueue() {
  return useQuery({
    queryKey: QUEUE_KEY,
    queryFn: () => apiClient.get('/sales/requests'),
    select: (data) => ({ requests: data.requests, canAssign: Boolean(data.canAssign) }),
  });
}

// GET /sales/requests/assignable-users — every active sales user, id + name
// only, for the reassign picker. Read-only, no permission gate on the read
// itself (see routes/pricingRoutes.js's own comment) — only fetched at all
// when canAssign is true, so this never fires for a salesperson who
// couldn't act on it anyway.
export function useAssignableSalesUsers(enabled) {
  return useQuery({
    queryKey: ['sales', 'assignableUsers'],
    queryFn: () => apiClient.get('/sales/requests/assignable-users'),
    select: (data) => data.users,
    enabled: Boolean(enabled),
  });
}

// POST /sales/requests/:requestId/assign — can_assign-gated reassignment
// from "My queue". Invalidates the queue (the reassigned request should
// disappear from THIS salesperson's own queue once it moves to someone
// else — requestRepository.listAssignedTo is scoped by assigned_to).
export function useAssignRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ requestId, assignedTo }) =>
      apiClient.post(`/sales/requests/${requestId}/assign`, { assignedTo }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUEUE_KEY });
    },
  });
}

export default useSalesQueue;
